import type { Business } from '../types/business';
import { isFeatureEnabled } from '../types/business';
import type { RolePermissions } from '../types/user';

const PAYMENT_PLAN_PERMISSION_KEYS: (keyof RolePermissions)[] = [
  'canViewPaymentPlans',
  'canCreatePaymentPlans',
  'canUpdatePaymentPlans',
  'canCancelPaymentPlans',
  'canCompletePaymentPlans',
  'canAddPaymentPlanPayments',
  'canVoidPaymentPlanPayments',
  'canCorrectPaymentPlanPayments',
];

const LOYALTY_PASS_PERMISSION_KEYS: (keyof RolePermissions)[] = [
  'canViewLoyaltyPass',
  'canManageLoyaltyPass',
];

const CATALOG_PACKS_PERMISSION_KEYS: (keyof RolePermissions)[] = [
  'canViewCatalogPacks',
  'canManageCatalogPacks',
];

const LICENCE_PAYMENT_PERMISSION_KEYS: (keyof RolePermissions)[] = ['canViewLicencePayment', 'canPayLicence'];

const CLIENT_TYPES_PERMISSION_KEYS: (keyof RolePermissions)[] = [
  'canViewClientTypes',
  'canCreateClientTypes',
  'canUpdateClientTypes',
  'canDeleteClientTypes',
];

const ACTIVITY_TYPES_PERMISSION_KEYS: (keyof RolePermissions)[] = [
  'canViewActivityTypes',
  'canCreateActivityTypes',
  'canUpdateActivityTypes',
  'canDeleteActivityTypes',
];

const OPPORTUNITY_CATEGORIES_PERMISSION_KEYS: (keyof RolePermissions)[] = [
  'canViewOpportunityCategories',
  'canCreateOpportunityCategories',
  'canUpdateOpportunityCategories',
  'canDeleteOpportunityCategories',
];

const EXPENSE_CATEGORIES_PERMISSION_KEYS: (keyof RolePermissions)[] = [
  'canViewExpenseCategories',
  'canCreateExpenseCategories',
  'canUpdateExpenseCategories',
  'canDeleteExpenseCategories',
];

const REFERENTS_PERMISSION_KEYS: (keyof RolePermissions)[] = [
  'canViewReferents',
  'canCreateReferents',
  'canUpdateReferents',
  'canDeleteReferents',
];

const MANUFACTURING_PERMISSION_KEYS: (keyof RolePermissions)[] = [
  'canViewManufacturingOrders',
  'canCreateManufacturingOrders',
  'canUpdateManufacturingOrders',
  'canCloseManufacturingOrders',
  'canDeleteManufacturingOrders',
];

const PICKUP_PERMISSION_KEYS: (keyof RolePermissions)[] = [
  'canViewPickups',
  'canManagePickups',
  'canUpdatePickupStatus',
  'canValidatePickup',
];

const SALE_CORRECTIONS_PERMISSION_KEYS: (keyof RolePermissions)[] = [
  'canVoidSales',
  'canCorrectSales',
  'canIssueSaleCredits',
];

export type BusinessFeaturePermissionGateOptions = {
  /** Super admin dans un business : garde les droits plans pour configurer / tester (le contrat Firestore reste appliqué côté serveur). */
  bypassPaymentPlansContract?: boolean;
};

/**
 * Applique les garde-fous « contrat business » sur les permissions issues du profil de rôle.
 */
export function applyBusinessFeaturePermissionGates(
  basePermissions: RolePermissions,
  business: Business | null,
  options?: BusinessFeaturePermissionGateOptions
): RolePermissions {
  let out = { ...basePermissions };

  if (!options?.bypassPaymentPlansContract) {
    if (!business || !isFeatureEnabled(business, 'paymentPlans')) {
      for (const key of PAYMENT_PLAN_PERMISSION_KEYS) {
        (out as Record<keyof RolePermissions, boolean>)[key] = false;
      }
    }
  }

  if (!business || !isFeatureEnabled(business, 'loyaltyPass')) {
    for (const key of LOYALTY_PASS_PERMISSION_KEYS) {
      (out as Record<keyof RolePermissions, boolean>)[key] = false;
    }
  }

  if (!business || !isFeatureEnabled(business, 'catalogPacks')) {
    for (const key of CATALOG_PACKS_PERMISSION_KEYS) {
      (out as Record<keyof RolePermissions, boolean>)[key] = false;
    }
  }

  if (!business || !isFeatureEnabled(business, 'licencePayment')) {
    for (const key of LICENCE_PAYMENT_PERMISSION_KEYS) {
      (out as Record<keyof RolePermissions, boolean>)[key] = false;
    }
  }

  if (!business || !isFeatureEnabled(business, 'clientTypes')) {
    for (const key of CLIENT_TYPES_PERMISSION_KEYS) {
      (out as Record<keyof RolePermissions, boolean>)[key] = false;
    }
  }

  if (!business || !isFeatureEnabled(business, 'activityTypes')) {
    for (const key of ACTIVITY_TYPES_PERMISSION_KEYS) {
      (out as Record<keyof RolePermissions, boolean>)[key] = false;
    }
  }

  if (!business || !isFeatureEnabled(business, 'opportunityCategories')) {
    for (const key of OPPORTUNITY_CATEGORIES_PERMISSION_KEYS) {
      (out as Record<keyof RolePermissions, boolean>)[key] = false;
    }
  }

  if (!business || !isFeatureEnabled(business, 'expenseCategories')) {
    for (const key of EXPENSE_CATEGORIES_PERMISSION_KEYS) {
      (out as Record<keyof RolePermissions, boolean>)[key] = false;
    }
  }

  if (!business || !isFeatureEnabled(business, 'referents')) {
    for (const key of REFERENTS_PERMISSION_KEYS) {
      (out as Record<keyof RolePermissions, boolean>)[key] = false;
    }
  }

  if (!business || !isFeatureEnabled(business, 'manufacturing')) {
    for (const key of MANUFACTURING_PERMISSION_KEYS) {
      (out as Record<keyof RolePermissions, boolean>)[key] = false;
    }
  }

  if (!business || !isFeatureEnabled(business, 'pickup')) {
    for (const key of PICKUP_PERMISSION_KEYS) {
      (out as Record<keyof RolePermissions, boolean>)[key] = false;
    }
  }

  if (!business || !isFeatureEnabled(business, 'pos')) {
    for (const key of SALE_CORRECTIONS_PERMISSION_KEYS) {
      (out as Record<keyof RolePermissions, boolean>)[key] = false;
    }
  }

  return out;
}
